import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ModalHot } from 'components/common/ModalHot';
import { PhoneValidationModal } from 'components/common/PhoneValidationModal';
import { ReasonContent } from './ReasonContent';
import { ConfirmContent } from './ConfirmContent';
import { useGenerateOtpMutation } from 'redux/api/users.api';
import { useCancelPaidCouponsMutation } from 'redux/api/commerce.api';
import { executeWithCaptcha, generateTestId } from 'app/functions';
import Recaptcha, { RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';
import { OTP_PROCESS, STATUS_MAP } from 'pages/personal-zone/utils/enums';
import { padId } from 'pages/personal-zone/utils/functions';
const STEPS = {
  REASON: 'reason',
  CONFIRM: 'confirm',
};

export const DeleteModal = ({
  open,
  onClose,
  onSuccess,
  onFailure,
  couponData, 
  testID,
}) => {
  const userData = useSelector((store) => store.config.UserData);
  
  const [step, setStep] = useState(STEPS.REASON);
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedReasonText, setSelectedReasonText] = useState('');
  
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [otpResponse, setOtpResponse] = useState(null);

  const [generateOtp, { isLoading: isGeneratingOtp }] = useGenerateOtpMutation();
  const [cancelCoupon, { isLoading: isCancelling }] = useCancelPaidCouponsMutation();

  useEffect(() => {
    if (!open) {
      setStep(STEPS.REASON);
      setSelectedReason('');
      setSelectedReasonText('');
      setIsOtpOpen(false);
      setOtpResponse(null);
    }
  }, [open]);

  const handleClose = () => {
    if (isGeneratingOtp || isCancelling) return;
    onClose();
  };

  const handleConfirmDelete = () => {
    executeWithCaptcha(async (token) => {
      try {
        const result = await generateOtp({
          userId: padId(userData?.user_id),
          cellphone: String(userData?.phone),
          process: OTP_PROCESS.VERIFY,
          'g-recaptcha-response': token
        }).unwrap();
        
        setOtpResponse(result);
        setIsOtpOpen(true);
      } catch (error) {
        console.error("Failed to generate OTP", error);
      }
    }, RECAPTCHA_ACTIONS.VERIFY_CODE);
  };

  const handleVerificationSuccess = async (otpResult) => {
    try {
      const result = await cancelCoupon({
        benefitId: couponData.benefitId,
        couponCodes: [String(couponData.code)],
        orderId: Number(couponData.orderNumber),
        reasonId: selectedReason,
        reasonText: selectedReasonText,
        sessionToken: otpResult.sessionToken || otpResult.token
      }).unwrap();

      setIsOtpOpen(false);
      if (onSuccess && result.successCoupons.length > 0) {
        const status = result.successCoupons[0].statusCoupon;
        onSuccess(result.message, STATUS_MAP[status]);
      }
      if (onFailure && result.successCoupons.length === 0) {
        onFailure(result.message);
      }
    } catch (error) {
      console.error("Failed to cancel coupon", error);
    }
  };

  return (
    <>
      <ModalHot
        open={open && !isOtpOpen}
        onClose={handleClose}
        testID={generateTestId(testID, 'modal')}
      >
        {({ isMobile }) => (
          step === STEPS.REASON ? (
            <ReasonContent
              selectedReason={selectedReason}
              onReasonChange={setSelectedReason}
              onReasonTextChange={setSelectedReasonText}
              onSubmit={() => setStep(STEPS.CONFIRM)}
              testID={generateTestId(testID, 'reason-content')}
              isMobile={isMobile}
            />
          ) : (
            <ConfirmContent
              onConfirm={handleConfirmDelete}
              onKeep={handleClose}
              testID={generateTestId(testID, 'confirm-content')}
              isMobile={isMobile}
              loading={isGeneratingOtp}
            />
          )
        )}
      </ModalHot>

      <PhoneValidationModal
        open={isOtpOpen}
        onClose={() => setIsOtpOpen(false)}
        phone={userData?.phone}
        userId={userData?.user_id}
        email={userData?.email}
        initialOtpResponse={otpResponse}
        onVerificationSuccess={handleVerificationSuccess}
        process={OTP_PROCESS.VERIFY}
        testID={`${testID}-phone-validation`}
      />

      {open && <Recaptcha isFunction={true} enterprise={true} onReady={() => {}} />}
    </>
  );
};

export default DeleteModal;